import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  type CustomFont,
  detectFontMeta,
  getFileFormat,
  reloadCustomFonts,
} from '../lib/fontLoader';

export interface PendingFontFile {
  file: File;
  fileName: string;
  format: string;
  detectedMeta: { weight: string; weightLabel: string; style: string; displayName: string };
  weight: string;
  style: string;
  displayName: string;
  status: 'pending' | 'uploading' | 'success' | 'error' | 'duplicate';
  error?: string;
}

interface FontStoreState {
  fonts: CustomFont[];
  familyName: string;
  pendingFiles: PendingFontFile[];
  isUploading: boolean;
  uploadProgress: number;
  uploadedCount: number;
  totalCount: number;
  isManagerOpen: boolean;

  setFamilyName: (name: string) => void;
  addFiles: (files: FileList | File[]) => void;
  removeFile: (index: number) => void;
  updateFileMeta: (index: number, updates: Partial<Pick<PendingFontFile, 'weight' | 'style' | 'displayName'>>) => void;
  clearPendingFiles: () => void;
  uploadFamily: () => Promise<{ success: number; failed: number; duplicates: number }>;
  deleteFont: (fontId: string) => Promise<void>;
  deleteFontFamily: (familyName: string) => Promise<void>;
  refreshFonts: () => Promise<void>;
  /**
   * Returns EVERY row in cs_fonts regardless of is_active. Used by the Font Manager
   * so admins can see and toggle inactive families. Does NOT mutate the store's
   * `fonts` array — that array remains the canonical "active fonts" list consumed
   * by FontPicker, FontMappingsTab, resolvers, etc.
   */
  fetchAllFonts: () => Promise<CustomFont[]>;
  updateFont: (fontId: string, updates: { fontFamily?: string; fontWeight?: string; fontStyle?: string; displayName?: string; postscriptName?: string }) => Promise<void>;
  renameFamily: (oldName: string, newName: string) => Promise<void>;
  setFontActive: (fontId: string, isActive: boolean) => Promise<void>;
  setFontFamilyActive: (familyName: string, isActive: boolean) => Promise<void>;
  openManager: () => void;
  closeManager: () => void;
}

export const useFontStore = create<FontStoreState>((set, get) => ({
  fonts: [],
  familyName: '',
  pendingFiles: [],
  isUploading: false,
  uploadProgress: 0,
  uploadedCount: 0,
  totalCount: 0,
  isManagerOpen: false,

  setFamilyName: (name) => set({ familyName: name }),

  addFiles: (files) => {
    const fileArray = Array.from(files);
    const validExtensions = ['woff2', 'woff', 'ttf', 'otf'];

    const newPending: PendingFontFile[] = fileArray
      .filter(file => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        return validExtensions.includes(ext);
      })
      .map(file => {
        const format = getFileFormat(file.name);
        const meta = detectFontMeta(file.name);
        return {
          file,
          fileName: file.name,
          format,
          detectedMeta: meta,
          weight: meta.weight,
          style: meta.style,
          displayName: meta.displayName,
          status: 'pending' as const,
        };
      });

    set(state => ({
      pendingFiles: [...state.pendingFiles, ...newPending],
    }));
  },

  removeFile: (index) => {
    set(state => ({
      pendingFiles: state.pendingFiles.filter((_, i) => i !== index),
    }));
  },

  updateFileMeta: (index, updates) => {
    set(state => ({
      pendingFiles: state.pendingFiles.map((f, i) =>
        i === index ? { ...f, ...updates } : f
      ),
    }));
  },

  clearPendingFiles: () => set({ pendingFiles: [], familyName: '' }),

  uploadFamily: async () => {
    const { familyName, pendingFiles } = get();
    if (!familyName.trim() || pendingFiles.length === 0) {
      return { success: 0, failed: 0, duplicates: 0 };
    }

    set({ isUploading: true, uploadProgress: 0, uploadedCount: 0, totalCount: pendingFiles.length });

    let success = 0;
    let failed = 0;
    let duplicates = 0;

    for (let i = 0; i < pendingFiles.length; i++) {
      const pending = pendingFiles[i];

      set(state => ({
        pendingFiles: state.pendingFiles.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading' } : f
        ),
      }));

      try {
        const storagePath = `${familyName.replace(/\s+/g, '-').toLowerCase()}/${pending.fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('cs-fonts')
          .upload(storagePath, pending.file, {
            contentType: `font/${pending.format}`,
            upsert: false,
          });

        if (uploadError) {
          if (uploadError.message?.includes('already exists') || uploadError.message?.includes('Duplicate')) {
            set(state => ({
              pendingFiles: state.pendingFiles.map((f, idx) =>
                idx === i ? { ...f, status: 'duplicate', error: 'File already exists in storage' } : f
              ),
            }));
            duplicates++;
            set({
              uploadedCount: i + 1,
              uploadProgress: Math.round(((i + 1) / pendingFiles.length) * 100),
            });
            continue;
          }
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('cs-fonts')
          .getPublicUrl(storagePath);

        const postscriptName = pending.fileName.replace(/\.(woff2|woff|ttf|otf)$/i, '');

        const { error: dbError } = await supabase
          .from('cs_fonts')
          .insert({
            font_family: familyName.trim(),
            font_weight: pending.weight,
            font_style: pending.style,
            font_display_name: `${familyName.trim()} ${pending.displayName}`,
            file_name: pending.fileName,
            file_url: urlData.publicUrl,
            file_format: pending.format,
            file_size: pending.file.size,
            is_active: true,
            postscript_name: postscriptName,
            storage_path: storagePath,
          });

        if (dbError) {
          if (dbError.code === '23505') {
            set(state => ({
              pendingFiles: state.pendingFiles.map((f, idx) =>
                idx === i ? { ...f, status: 'duplicate', error: `${familyName} ${pending.displayName} already registered` } : f
              ),
            }));
            duplicates++;
            await supabase.storage.from('cs-fonts').remove([storagePath]);
            set({
              uploadedCount: i + 1,
              uploadProgress: Math.round(((i + 1) / pendingFiles.length) * 100),
            });
            continue;
          }
          throw dbError;
        }

        set(state => ({
          pendingFiles: state.pendingFiles.map((f, idx) =>
            idx === i ? { ...f, status: 'success' } : f
          ),
        }));
        success++;
      } catch (err: any) {
        console.error(`[FontStore] Failed to upload ${pending.fileName}:`, err);
        set(state => ({
          pendingFiles: state.pendingFiles.map((f, idx) =>
            idx === i ? { ...f, status: 'error', error: err.message || 'Upload failed' } : f
          ),
        }));
        failed++;
      }

      set({
        uploadedCount: i + 1,
        uploadProgress: Math.round(((i + 1) / pendingFiles.length) * 100),
      });
    }

    await reloadCustomFonts();
    await get().refreshFonts();

    set({ isUploading: false });

    return { success, failed, duplicates };
  },

  deleteFont: async (fontId) => {
    const font = get().fonts.find(f => f.id === fontId);
    if (!font) return;

    const storagePath = font.storagePath || `${font.fontFamily.replace(/\s+/g, '-').toLowerCase()}/${font.fileName}`;
    await supabase.storage.from('cs-fonts').remove([storagePath]);
    await supabase.from('cs_fonts').delete().eq('id', fontId);

    await reloadCustomFonts();
    await get().refreshFonts();
  },

  deleteFontFamily: async (familyName) => {
    const fontsInFamily = get().fonts.filter(f => f.fontFamily === familyName);

    const storagePaths = fontsInFamily.map(f =>
      f.storagePath || `${f.fontFamily.replace(/\s+/g, '-').toLowerCase()}/${f.fileName}`
    );
    if (storagePaths.length > 0) {
      await supabase.storage.from('cs-fonts').remove(storagePaths);
    }

    await supabase.from('cs_fonts').delete().eq('font_family', familyName);

    await reloadCustomFonts();
    await get().refreshFonts();
  },

  refreshFonts: async () => {
    const { data } = await supabase
      .from('cs_fonts')
      .select('*')
      .eq('is_active', true)
      .order('font_family')
      .order('font_weight');

    const fonts: CustomFont[] = (data || []).map((row: any) => ({
      id: row.id,
      fontFamily: row.font_family,
      fontWeight: row.font_weight,
      fontStyle: row.font_style,
      displayName: row.font_display_name,
      fileName: row.file_name,
      fileUrl: row.file_url,
      fileFormat: row.file_format,
      fileSize: row.file_size,
      isActive: row.is_active,
      postscriptName: row.postscript_name || row.file_name.replace(/\.(woff2|woff|ttf|otf)$/i, ''),
      serverAvailable: row.server_available !== false,
      storagePath: row.storage_path || null,
    }));

    set({ fonts });
  },

  fetchAllFonts: async () => {
    const { data } = await supabase
      .from('cs_fonts')
      .select('*')
      .order('font_family')
      .order('font_weight');
    return (data || []).map((row: any) => ({
      id: row.id,
      fontFamily: row.font_family,
      fontWeight: row.font_weight,
      fontStyle: row.font_style,
      displayName: row.font_display_name,
      fileName: row.file_name,
      fileUrl: row.file_url,
      fileFormat: row.file_format,
      fileSize: row.file_size,
      isActive: row.is_active !== false,
      postscriptName:
        row.postscript_name || row.file_name.replace(/\.(woff2|woff|ttf|otf)$/i, ''),
      serverAvailable: row.server_available !== false,
      storagePath: row.storage_path || null,
    }));
  },

  updateFont: async (fontId, updates) => {
    const dbUpdates: Record<string, string> = {};
    if (updates.fontFamily !== undefined) dbUpdates.font_family = updates.fontFamily;
    if (updates.fontWeight !== undefined) dbUpdates.font_weight = updates.fontWeight;
    if (updates.fontStyle !== undefined) dbUpdates.font_style = updates.fontStyle;
    if (updates.displayName !== undefined) dbUpdates.font_display_name = updates.displayName;
    if (updates.postscriptName !== undefined) dbUpdates.postscript_name = updates.postscriptName;
    if (Object.keys(dbUpdates).length === 0) return;
    await supabase.from('cs_fonts').update(dbUpdates).eq('id', fontId);
    await reloadCustomFonts();
    await get().refreshFonts();
  },

  renameFamily: async (oldName, newName) => {
    if (!newName.trim() || newName.trim() === oldName) return;
    await supabase
      .from('cs_fonts')
      .update({ font_family: newName.trim() })
      .eq('font_family', oldName);
    await reloadCustomFonts();
    await get().refreshFonts();
  },

  setFontActive: async (fontId, isActive) => {
    await supabase.from('cs_fonts').update({ is_active: isActive }).eq('id', fontId);
    await reloadCustomFonts();
    await get().refreshFonts();
  },

  setFontFamilyActive: async (familyName, isActive) => {
    await supabase
      .from('cs_fonts')
      .update({ is_active: isActive })
      .eq('font_family', familyName);
    await reloadCustomFonts();
    await get().refreshFonts();
  },

  openManager: () => set({ isManagerOpen: true }),
  closeManager: () => set({ isManagerOpen: false }),
}));
